import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, normalizeStyle as _normalizeStyle, renderList as _renderList, Fragment as _Fragment, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createVNode as _createVNode } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6" }
const _hoisted_2 = { class: "mb-6 sm:mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" }
const _hoisted_3 = {
  key: 0,
  class: "grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
}
const _hoisted_4 = {
  key: 1,
  class: "bg-white dark:bg-black rounded-xl shadow-lg p-8 sm:p-12 text-center"
}
const _hoisted_5 = { class: "max-w-md mx-auto" }
const _hoisted_6 = {
  key: 2,
  class: "grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 sm:gap-5 lg:gap-6"
}

import { ref, onMounted, computed } from 'vue';
import { useVehicleStore } from '@/stores/vehicleStore';
import { useBrand } from '@/composables/brand/useBrand';
import { useToast } from 'vue-toastification';
import VehicleCard from '@/components/customer/VehicleCard.vue';
import AddVehicleModal from '@/components/customer/AddVehicleModal.vue';
import EditVehicleModal from '@/components/customer/EditVehicleModal.vue';
import ViewVehicleModal from '@/components/customer/ViewVehicleModal.vue';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue';
import type { Vehicle, VehicleFormData } from '@/types/vehicle';


export default /*@__PURE__*/_defineComponent({
  __name: 'VehicleInfoView',
  setup(__props) {

const vehicleStore = useVehicleStore();
const { brandColors } = useBrand();
const toast = useToast();

// State
const showAddModal = ref(false);
const showEditModal = ref(false);
const showViewModal = ref(false);
const showDeleteModal = ref(false);
const selectedVehicle = ref<Vehicle | null>(null);
const modalLoading = ref(false);
const addVehicleModalRef = ref<InstanceType<typeof AddVehicleModal> | null>(null);

// Computed
const vehicles = computed(() => vehicleStore.vehicles);
const loading = computed(() => vehicleStore.loading);

// Lifecycle
onMounted(async () => {
  await loadVehicles();
});

// Methods
async function loadVehicles() {
  try {
    await vehicleStore.fetchVehicles();
  } catch (error) {
    toast.error('Failed to load vehicles');
  }
}

// Add Modal
function openAddModal() {
  showAddModal.value = true;
}

function closeAddModal() {
  showAddModal.value = false;
}

async function handleAddVehicle(data: VehicleFormData, photoFiles: File[], photoSides: string[]) {
  modalLoading.value = true;
  try {
    await vehicleStore.createVehicle(data, photoFiles, photoSides);
    toast.success('Vehicle added successfully');
    addVehicleModalRef.value?.resetForm(); // Reset form after successful save
    closeAddModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to add vehicle');
  } finally {
    modalLoading.value = false;
  }
}

// Edit Modal
function openEditModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showEditModal.value = true;
}

function closeEditModal() {
  showEditModal.value = false;
  selectedVehicle.value = null;
}

async function handleEditVehicle(vehicleId: number, data: VehicleFormData, photoFiles: File[], photoSides: string[], deletedPhotoIds: number[]) {
  modalLoading.value = true;
  try {
    // Delete photos marked for deletion
    for (const photoId of deletedPhotoIds) {
      await vehicleStore.deleteVehiclePhoto(photoId);
    }

    // Update vehicle with new data and photos
    await vehicleStore.updateVehicle(vehicleId, data, photoFiles, photoSides);

    toast.success('Vehicle updated successfully');
    closeEditModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to update vehicle');
  } finally {
    modalLoading.value = false;
  }
}

// View Modal
function openViewModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showViewModal.value = true;
}

function closeViewModal() {
  showViewModal.value = false;
  selectedVehicle.value = null;
}

function handleEditFromView(vehicle: Vehicle) {
  closeViewModal();
  openEditModal(vehicle);
}

// Delete Modal
function openDeleteModal(vehicle: Vehicle) {
  selectedVehicle.value = vehicle;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  selectedVehicle.value = null;
}

async function handleDeleteVehicle() {
  if (!selectedVehicle.value) return;

  modalLoading.value = true;
  try {
    await vehicleStore.deleteVehicle(selectedVehicle.value.vehicle_id);
    toast.success('Vehicle deleted successfully');
    closeDeleteModal();
  } catch (error: any) {
    toast.error(error.response?.data?.message || 'Failed to delete vehicle');
  } finally {
    modalLoading.value = false;
  }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[1] || (_cache[1] = _createElementVNode("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "My Vehicles", -1)),
      _createElementVNode("button", {
        onClick: openAddModal,
        class: "inline-flex items-center px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-150 shadow-md hover:shadow-lg",
        style: _normalizeStyle({ backgroundColor: _unref(brandColors).primary })
      }, _cache[0] || (_cache[0] = [
        _createElementVNode("svg", {
          xmlns: "http://www.w3.org/2000/svg",
          class: "h-5 w-5 mr-2",
          fill: "none",
          viewBox: "0 0 24 24",
          stroke: "currentColor"
        }, [
          _createElementVNode("path", {
            "stroke-linecap": "round",
            "stroke-linejoin": "round",
            "stroke-width": "2",
            d: "M12 4v16m8-8H4"
          })
        ], -1),
        _createTextVNode(" Add New Vehicle ")
      ]), 4)
    ]),
    (loading.value && vehicles.value.length === 0)
      ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(4, (i) => {
            return _createElementVNode("div", {
              key: i,
              class: "animate-pulse"
            }, _cache[2] || (_cache[2] = [
              _createElementVNode("div", { class: "bg-gray-200 dark:bg-gray-800 rounded-xl h-64" }, null, -1)
            ]))
          }), 64))
        ]))
      : (!loading.value && vehicles.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", {
                class: "w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-6 rounded-full flex items-center justify-center",
                style: _normalizeStyle({ backgroundColor: _unref(brandColors).primary + '20' })
              }, [
                (_openBlock(), _createElementBlock("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "h-10 w-10 sm:h-12 sm:w-12",
                  style: _normalizeStyle({ color: _unref(brandColors).primary }),
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor"
                }, _cache[3] || (_cache[3] = [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M19 9l-7 7-7-7"
                  }, null, -1),
                  _createElementVNode("path", {
                    d: "M3 13.5L5 21h14l2-7.5M3 13.5L5 6h14l2 7.5M3 13.5h18M7 21v-3m10 3v-3",
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2"
                  }, null, -1)
                ]), 4))
              ], 4),
              _cache[4] || (_cache[4] = _createElementVNode("h2", { class: "text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3" }, " No Vehicles Added Yet ", -1)),
              _cache[5] || (_cache[5] = _createElementVNode("p", { class: "text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6" }, " Start by adding your first vehicle to register tires and submit claims. ", -1))
            ])
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(vehicles.value, (vehicle) => {
              return (_openBlock(), _createBlock(VehicleCard, {
                key: vehicle.vehicle_id,
                vehicle: vehicle,
                onView: openViewModal,
                onEdit: openEditModal,
                onDelete: openDeleteModal
              }, null, 8, ["vehicle"]))
            }), 128))
          ])),
    _createVNode(AddVehicleModal, {
      ref_key: "addVehicleModalRef",
      ref: addVehicleModalRef,
      "is-open": showAddModal.value,
      loading: modalLoading.value,
      onClose: closeAddModal,
      onSave: handleAddVehicle
    }, null, 8, ["is-open", "loading"]),
    _createVNode(EditVehicleModal, {
      "is-open": showEditModal.value,
      vehicle: selectedVehicle.value,
      loading: modalLoading.value,
      onClose: closeEditModal,
      onSave: handleEditVehicle
    }, null, 8, ["is-open", "vehicle", "loading"]),
    _createVNode(ViewVehicleModal, {
      "is-open": showViewModal.value,
      vehicle: selectedVehicle.value,
      onClose: closeViewModal,
      onEdit: handleEditFromView
    }, null, 8, ["is-open", "vehicle"]),
    _createVNode(DeleteConfirmationModal, {
      "is-open": showDeleteModal.value,
      "is-loading": modalLoading.value,
      "entity-name": `${selectedVehicle.value?.vehicle_make} ${selectedVehicle.value?.vehicle_model} (${selectedVehicle.value?.plate_number})`,
      "entity-type": "vehicle",
      onClose: closeDeleteModal,
      onConfirm: handleDeleteVehicle
    }, null, 8, ["is-open", "is-loading", "entity-name"])
  ]))
}
}

})